import { LLMConfig, ChatMessage } from '../../../types/llm'
import { LLMProviderInterface } from '../llm-service'

interface OpenAIResponse {
  choices?: Array<{
    message?: {
      content?: string
    }
  }>
  content?: string
  text?: string
  response?: string
}

export class CustomProvider implements LLMProviderInterface {
  private config: LLMConfig

  constructor(config: LLMConfig) {
    this.config = config
    if (!config.baseUrl) {
      throw new Error('Custom provider requires a base URL')
    }
  }

  async chat(messages: ChatMessage[], config?: Partial<LLMConfig>): Promise<string> {
    // Use Cloudflare Function proxy to avoid CORS issues
    const response = await fetch('/api/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        provider: 'custom',
        messages: messages.map(m => ({
          role: m.role,
          content: m.content
        })),
        config: {
          apiKey: this.config.apiKey,
          baseUrl: this.config.baseUrl,
          model: config?.model || this.config.model,
          temperature: config?.temperature || this.config.temperature || 0.7,
          maxTokens: config?.maxTokens || this.config.maxTokens || 2000,
          stream: false
        }
      })
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Custom API error: ${error}`)
    }

    const data = await response.json() as OpenAIResponse
    // Try different response formats - Venice returns structured JSON
    if (data.choices?.[0]?.message?.content) {
      const content = data.choices[0].message.content
      
      // Check if content is a JSON string (Venice JSON schema response)
      if (typeof content === 'string' && content.trim().startsWith('{')) {
        try {
          const parsedContent = JSON.parse(content)
          console.log('Venice parsed JSON:', parsedContent)
          const result = parsedContent.message || content
          console.log('Returning message:', result)
          return result
        } catch (e) {
          // If parsing fails, return the string as-is
          console.log('Failed to parse JSON, returning as-is:', content)
          return content
        }
      } else if (typeof content === 'object' && content !== null) {
        // Venice JSON schema format as object - extract message field
        console.log('Venice content as object:', content)
        const result = (content as any).message || JSON.stringify(content)
        console.log('Returning from object:', result)
        return result
      } else {
        // Standard string format
        console.log('Standard string content:', content)
        return content as string
      }
    } else {
      // Fallback formats
      return data.content || 
             data.text || 
             data.response ||
             JSON.stringify(data)
    }
  }

  async streamChat(
    messages: ChatMessage[],
    config?: Partial<LLMConfig>,
    callbacks?: {
      onToken?: (token: string) => void
      onComplete?: (fullText: string) => void
      onError?: (error: Error) => void
    }
  ): Promise<void> {
    try {
      // Use Cloudflare Function proxy to avoid CORS issues
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: 'custom',
          messages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          config: {
            apiKey: this.config.apiKey,
            baseUrl: this.config.baseUrl,
            model: config?.model || this.config.model,
            temperature: config?.temperature || this.config.temperature || 0.7,
            maxTokens: config?.maxTokens || this.config.maxTokens || 2000,
            stream: true
          }
        })
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`Custom API error: ${error}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let fullText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              callbacks?.onComplete?.(fullText)
              continue
            }

            try {
              const parsed = JSON.parse(data) as any
              // Try different streaming formats
              const content = parsed.choices?.[0]?.delta?.content ||
                            parsed.delta?.content ||
                            parsed.content ||
                            parsed.text

              if (content) {
                fullText += content
                callbacks?.onToken?.(content)
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      if (fullText) {
        callbacks?.onComplete?.(fullText)
      }
    } catch (error) {
      callbacks?.onError?.(error as Error)
      throw error
    }
  }
}
